/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ChevronRight,
  Clock, Download, FileText, Info, Loader2, ShieldAlert,
} from "lucide-react";
import axios from "axios";

import { track } from "@/lib/tracking";

/**
 * Free EU horse transport document generator.
 *
 * Three-step wizard:
 *   1. NVWA pre-flight checklist (shown only for cross-border EU transport)
 *   2. Personal / horse / vehicle details
 *   3. Multi-page A4 PDF (cover + data + dedicated NVWA-checklist + signatures)
 *
 * The PDF itself is rendered server-side by the FastAPI backend
 * (`POST /api/tools/transport-pdf` → ReportLab) so the output is byte-identical
 * to the mobile app's in-app generator. Frontend only collects data + triggers
 * the download.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: "NL", name: "Nederland", regulator: "NVWA", url: "https://www.nvwa.nl/onderwerpen/transport-van-dieren" },
  { code: "BE", name: "België",    regulator: "FAVV / AFSCA", url: "https://www.favv-afsca.be/dierengezondheid/" },
  { code: "DE", name: "Duitsland", regulator: "BMEL",  url: "https://www.bmel.de" },
  { code: "FR", name: "Frankrijk", regulator: "Ministère de l’Agriculture", url: "https://agriculture.gouv.fr" },
  { code: "LU", name: "Luxemburg", regulator: "ASV",   url: "https://agriculture.public.lu" },
  { code: "ES", name: "Spanje",    regulator: "MAPA",  url: "https://www.mapa.gob.es" },
  { code: "IT", name: "Italië",    regulator: "Ministero della Salute", url: "https://www.salute.gov.it" },
  { code: "PT", name: "Portugal",  regulator: "DGAV",  url: "https://www.dgav.pt" },
  { code: "AT", name: "Oostenrijk", regulator: "BMSGPK", url: "https://www.sozialministerium.at" },
  { code: "DK", name: "Denemarken", regulator: "Fødevarestyrelsen", url: "https://www.foedevarestyrelsen.dk" },
  { code: "SE", name: "Zweden",    regulator: "Jordbruksverket", url: "https://www.jordbruksverket.se" },
  { code: "PL", name: "Polen",     regulator: "GIW",   url: "https://www.wetgiw.gov.pl" },
  { code: "IE", name: "Ierland",   regulator: "DAFM",  url: "https://www.gov.ie/en/organisation/department-of-agriculture-food-and-the-marine/" },
  { code: "NO", name: "Noorwegen (EER)", regulator: "Mattilsynet", url: "https://www.mattilsynet.no" },
  { code: "CH", name: "Zwitserland", regulator: "BLV",  url: "https://www.blv.admin.ch" },
];

const REASONS = [
  { key: "sale",          label: "Verkoop",          economic: true },
  { key: "purchase",      label: "Aankoop",          economic: true },
  { key: "competition",   label: "Wedstrijd",        economic: false },
  { key: "training",      label: "Training",         economic: false },
  { key: "vet",           label: "Dierenarts",       economic: false },
  { key: "breeding",      label: "Dekking / fokkerij", economic: true },
  { key: "boarding",      label: "Pensionstal",      economic: true },
  { key: "inspection",    label: "Keuring",          economic: true },
  { key: "moving",        label: "Verhuizing",       economic: false },
  { key: "other",         label: "Overig",           economic: null },
];

const DATA_SECTIONS = [
  {
    key: "horse",
    label: "Gegevens paard",
    fields: [
      { name: "horseName", label: "Paardnaam", required: true },
      { name: "ueln", label: "UELN nummer", placeholder: "528003201000000" },
      { name: "chip", label: "Chipnummer", placeholder: "528 21000 12345 678" },
      { name: "sex", label: "Geslacht", type: "select", options: ["Hengst", "Ruin", "Merrie", "Veulen"] },
      { name: "birthdate", label: "Geboortedatum", type: "date" },
      { name: "breed", label: "Ras", placeholder: "Bv. KWPN, Friese" },
      { name: "color", label: "Kleur", placeholder: "Bv. vos, bruin, schimmel" },
    ],
  },
  {
    key: "owner",
    label: "Eigenaar / verantwoordelijke",
    fields: [
      { name: "ownerName", label: "Naam", required: true },
      { name: "ownerAddress", label: "Adres" },
      { name: "ownerZip", label: "Postcode" },
      { name: "ownerCity", label: "Plaats" },
      { name: "ownerPhone", label: "Telefoon", type: "tel" },
      { name: "ownerEmail", label: "E-mailadres", type: "email" },
    ],
  },
  {
    key: "transporter",
    label: "Vervoerder",
    fields: [
      { name: "transporterName", label: "Naam" },
      { name: "transporterAddress", label: "Adres" },
      { name: "transporterPhone", label: "Telefoon", type: "tel" },
    ],
  },
  {
    key: "transport",
    label: "Transport",
    fields: [
      { name: "date", label: "Datum", type: "date", required: true },
      { name: "time", label: "Vertrektijd", type: "time" },
      { name: "arrivalTime", label: "Verwachte aankomsttijd", type: "time" },
      { name: "origin", label: "Vertreklocatie", required: true },
      { name: "destination", label: "Bestemming", required: true },
      { name: "distance", label: "Afstand (km)", type: "number", placeholder: "Bv. 120" },
      { name: "reason", label: "Doel van het transport", type: "select", options: REASONS.map((r) => r.label), required: true },
    ],
  },
  {
    key: "vehicle",
    label: "Voertuig",
    fields: [
      { name: "vehiclePlate", label: "Kenteken trekker" },
      { name: "trailerPlate", label: "Kenteken trailer" },
      { name: "driverName", label: "Naam chauffeur" },
    ],
  },
  {
    key: "eu",
    label: "EU-internationaal transport",
    euOnly: true,
    fields: [
      { name: "passportNumber", label: "Paardenpaspoort-nummer", placeholder: "NL12345678" },
      { name: "healthCertificateNumber", label: "Gezondheidscertificaat (INTRA) nummer", placeholder: "Bv. INTRA.NL.2026.0001234" },
      { name: "tracesNumber", label: "TRACES NT registratienummer", placeholder: "Bv. BE-TRC-2026-12345" },
      { name: "nvwaClientNumber", label: "NVWA-klantnummer", placeholder: "Bv. 12345678" },
      { name: "originUbn", label: "UBN vertreklocatie", placeholder: "Bv. 1234567" },
      { name: "exportInspectionDate", label: "Datum exportkeuring NVWA", type: "date" },
    ],
  },
];

/**
 * NVWA pre-flight checklist questions. The answers drive smart warnings + an
 * auto-derived list of documents that the user must arrange before leaving.
 */
const CHECKLIST = [
  {
    name: "chip",
    label: "Is het paard gechipt en is het chipnummer geregistreerd in I&R (RVO)?",
    help: "Verplicht voor elk EU-transport. Geen chip = aanvraag chip via een RVO-erkend chipper, vooraf afronden.",
    type: "yesno",
  },
  {
    name: "passport",
    label: "Heeft het paard een geldig paardenpaspoort dat overeenkomt met de chip?",
    help: "Gegevens in paspoort moeten overeenkomen met het signalement van het paard én de chip. Anders weigert NVWA-dierenarts.",
    type: "yesno",
  },
  {
    name: "ir",
    label: "Staat het paard correct geregistreerd in de I&R-database (RVO)?",
    help: "Tijdelijk in NL? Toon dan het inkomende diergezondheidscertificaat (max. 30 of 90 dagen geldig).",
    type: "yesno",
  },
  {
    name: "registered",
    label: "Is het een geregistreerd paard (stamboek of FEI-paspoort/FEI Recognition Card)?",
    help: "Registered = stamboekpaard of FEI-paspoort. Geregistreerde paarden vallen onder soepeler eisen (ander certificaatmodel + 30-dagen-certificaat mogelijk).",
    type: "yesno-unknown",
  },
  {
    name: "ubn",
    label: "Heeft de vertreklocatie een Uniek Bedrijfsnummer (UBN)?",
    help: "UBN is verplicht en moet in TRACES geregistreerd staan. Aanvragen kan via RVO.",
    type: "yesno",
  },
  {
    name: "vaccinations",
    label: "Eist de bestemming / het evenement specifieke vaccinaties?",
    help: "Vaccinatie is EU-rechtelijk niet verplicht, maar wedstrijden/evenementen kunnen het wel eisen. Vermeld vaccinaties in het paspoort.",
    type: "yesno-na",
  },
];

const TRANSPORT_TYPES = [
  { value: "private",   label: "Particulier — eigen paard, geen vergoeding" },
  { value: "commercial", label: "Economisch / commercieel — verkoop, fok, handel of betaald vervoer" },
];

const DURATIONS = [
  { value: "lt_65km",   label: "Korter dan 65 km" },
  { value: "lt_8h",     label: "65 km – minder dan 8 uur" },
  { value: "gt_8h",     label: "Langer dan 8 uur" },
];

const TRACES_TYPES = [
  { value: "10d",        label: "Standaard 10-dagen TRACES-certificaat" },
  { value: "30d_fei",    label: "30-dagen-certificaat (alleen FEI-paspoort)" },
];

const genDocNumber = () => {
  const d = new Date();
  const stamp = d.toISOString().slice(0, 10).replace(/-/g, "");
  const rand = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `ES-${stamp}-${rand}`;
};

// Derive the set of required transport documents from checklist answers.
// Returns an array of { ok: bool, text } items rendered in the warning panel
// and printed as the "Verplichte documenten" section in the PDF.
const deriveTransportDocs = (cl) => {
  const out = [];
  if (!cl.transportType) return out;

  const isPrivate = cl.transportType === "private";
  const dur = cl.duration;

  if (dur === "lt_65km") {
    if (isPrivate) {
      out.push({ ok: true, text: "Geen vervoerdersvergunning vereist (<65 km, particulier)." });
      out.push({ ok: false, text: "Vraag bij grensoverschrijding een basisregistratie als vervoerder onder AHR aan (NVWA)." });
    } else {
      out.push({ ok: false, text: "Basisregistratie als vervoerder onder AHR is verplicht voor commercieel <65 km transport." });
    }
  } else if (dur === "lt_8h") {
    out.push({ ok: false, text: "Vervoerdersvergunning Type I vereist (65 km – <8 u)." });
    out.push({ ok: false, text: "Getuigschrift van vakbekwaamheid van de chauffeur is verplicht." });
  } else if (dur === "gt_8h") {
    out.push({ ok: false, text: "Vervoerdersvergunning Type II vereist (>8 u)." });
    out.push({ ok: false, text: "Getuigschrift van vakbekwaamheid van de chauffeur." });
    out.push({ ok: false, text: "Certificaat van goedkeuring vervoermiddel (NVWA)." });
    if (cl.registered === "yes") {
      out.push({ ok: true, text: "Reisplan + GPS niet vereist (geregistreerd paard, FEI/stamboek)." });
    } else {
      out.push({ ok: false, text: "Reisplan (journaal) ingediend + actieve GPS in voertuig vereist." });
    }
  }

  // Always required for any cross-border EU transport
  out.push({ ok: cl.chip === "yes", text: "Chip aanwezig en geregistreerd in I&R (RVO)." });
  out.push({ ok: cl.passport === "yes", text: "Geldig paardenpaspoort dat overeenkomt met chip + signalement." });
  out.push({ ok: cl.ir === "yes", text: "I&R-registratie actief (RVO)." });
  out.push({ ok: cl.ubn === "yes", text: "UBN van vertreklocatie geregistreerd in TRACES." });
  out.push({ ok: true, text: "Diergezondheidscertificaat (TRACES) aanvragen via MijnNVWA → exportkeuring binnen 48u vóór vertrek." });
  out.push({ ok: true, text: "Verklaring door de Exploitant (NVWA bijlage 1 paarden) — uitgeprint, ingevuld en ondertekend klaarleggen voor de NVWA-dierenarts." });
  return out;
};

const TransportDocTool = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState("NL");
  const [destinationCountry, setDestinationCountry] = useState("NL");
  const [data, setData] = useState({});
  const [checklist, setChecklist] = useState({
    transportType: "",
    duration: "",
    tracesType: "10d",
    departWithin48h: false,
    tracesDestinationValid: false,
  });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const c = useMemo(() => COUNTRIES.find((x) => x.code === country), [country]);
  const dest = useMemo(() => COUNTRIES.find((x) => x.code === destinationCountry), [destinationCountry]);
  const isEuTransport = destinationCountry !== country;

  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const setCl = (k, v) => setChecklist((p) => ({ ...p, [k]: v }));

  // Sync the wizard step with EU/non-EU mode:
  // - non-EU: skip checklist, jump to data form (step 2)
  // - EU detected (and not yet past data form): show checklist first
  useEffect(() => {
    if (!isEuTransport && step === 1) {
      setStep(2);
    } else if (isEuTransport && step === 2 && !success) {
      // user just switched to EU mode — surface the checklist
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEuTransport]);

  // Auto-reset TRACES certificate type when the horse is no longer registered.
  // Without this the 30-day FEI label would persist in state (and end up in the PDF)
  // even after the option became disabled in the dropdown.
  useEffect(() => {
    if (checklist.registered !== "yes" && checklist.tracesType === "30d_fei") {
      setChecklist((p) => ({ ...p, tracesType: "10d" }));
    }
  }, [checklist.registered, checklist.tracesType]);

  const derivedDocs = useMemo(() => deriveTransportDocs(checklist), [checklist]);

  const checklistComplete = useMemo(() => {
    if (!isEuTransport) return true;
    const required = ["chip", "passport", "ir", "registered", "ubn", "vaccinations", "transportType", "duration"];
    return required.every((k) => checklist[k]);
  }, [checklist, isEuTransport]);

  const missing = useMemo(() => {
    const req = DATA_SECTIONS
      .filter((s) => !s.euOnly || isEuTransport)
      .flatMap((s) => s.fields)
      .filter((f) => f.required);
    return req.filter((f) => !data[f.name]?.toString().trim()).map((f) => f.label);
  }, [data, isEuTransport]);

  const reason = REASONS.find((r) => r.label === data.reason);

  const warning = useMemo(() => {
    if (isEuTransport && step >= 2) {
      return {
        tone: "warn",
        text: `Internationaal transport van ${c.name} naar ${dest?.name}. Volgens de NVWA-stappenplan moet je 6 weken vóór vertrek beginnen met de voorbereidingen.`,
      };
    }
    if (!reason) return null;
    if (reason.economic === false) {
      return {
        tone: "info",
        text: `Particulier vervoer naar ${reason.label.toLowerCase()} met je eigen paard valt doorgaans buiten de Transportverordening volgens de ${c.regulator}. Dit document wordt vrijwillig aangemaakt voor administratie.`,
      };
    }
    if (reason.economic === true) {
      return {
        tone: "warn",
        text: `Economisch transport (${reason.label.toLowerCase()}): aanvullende eisen via de ${c.regulator}, o.a. vervoerdersvergunning Type 1 of 2 bij ritten >65 km en specifieke welzijnseisen.`,
      };
    }
    return null;
  }, [reason, c, dest, isEuTransport, step]);

  // -------- PDF GENERATION (backend ReportLab — matches mobile app) --------
  const generate = async () => {
    if (missing.length) return;
    setBusy(true);
    setSuccess(false);
    try {
      const payload = {
        country,
        destination_country: destinationCountry,
        data,
        checklist: isEuTransport ? checklist : null,
        derived_docs: isEuTransport ? derivedDocs : null,
      };

      const res = await axios.post(`${API}/api/tools/transport-pdf`, payload, {
        responseType: "blob",
        headers: { "Content-Type": "application/json" },
        validateStatus: () => true,
      });

      if (res.status !== 200) {
        let detail = "Onbekende fout";
        try {
          const txt = await res.data.text();
          detail = JSON.parse(txt).detail || detail;
        } catch { /* keep generic */ }
        throw new Error(detail);
      }

      const docNumber = res.headers["x-doc-number"] || genDocNumber();
      const filename = `Vervoersdocument-${(data.horseName || "paard").replace(/[^a-zA-Z0-9_-]+/g, "-")}-${docNumber}.pdf`;
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

      // Tracking
      track("tool_generate", {
        label: "transport-doc",
        props: {
          country,
          destinationCountry,
          eu_transport: isEuTransport,
          reason: data.reason,
          has_email: Boolean(data.ownerEmail?.trim()),
          economic: reason?.economic ?? null,
          transport_type: checklist.transportType || null,
          duration: checklist.duration || null,
          traces_type: isEuTransport ? checklist.tracesType : null,
          checklist_ok: isEuTransport ? CHECKLIST.every((q) => ["yes", "na"].includes(checklist[q.name])) : null,
        },
      });

      // Lead capture (best-effort)
      const ownerEmail = (data.ownerEmail || "").trim();
      if (ownerEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail)) {
        try {
          await axios.post(`${API}/api/waitlist`, {
            email: ownerEmail,
            role: "transport-doc-tool",
            utm_source: "tools",
            utm_campaign: isEuTransport ? "vervoersdocument-paard-eu" : "vervoersdocument-paard",
          });
        } catch { /* silent */ }
      }

      setSuccess(true);
      setStep(isEuTransport ? 3 : 2);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[transport-doc] PDF generation failed:", err);
      alert(`PDF genereren mislukt: ${err?.message || "onbekende fout"}`);
    } finally {
      setBusy(false);
    }
  };

  // -------- RENDER ----------------------------------------------------------
  const stepNumber = isEuTransport ? step : Math.max(step - 1, 1);
  const totalSteps = isEuTransport ? 3 : 2;

  return (
    <div
      data-testid="transport-doc-tool"
      className="overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 sm:p-10"
    >
      {/* Top: country + destination */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--forest))]/8 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--forest))]">
          <FileText className="h-3 w-3" strokeWidth={1.8} /> Gratis generator
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-soft))]">
            Vertrek
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="transport-doc-country"
              className="rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-1.5 text-[12px] font-medium text-[hsl(var(--ink))]"
            >
              {COUNTRIES.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-soft))]">
            Bestemming
            <select
              value={destinationCountry}
              onChange={(e) => setDestinationCountry(e.target.value)}
              data-testid="transport-doc-destination-country"
              className="rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-1.5 text-[12px] font-medium text-[hsl(var(--ink))]"
            >
              {COUNTRIES.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
            </select>
          </label>
        </div>
      </div>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
        <span data-testid="transport-doc-step-indicator">Stap {stepNumber} van {totalSteps}</span>
        <ChevronRight className="h-3 w-3" />
        <span className="font-semibold text-[hsl(var(--forest))]">
          {step === 1 ? "NVWA-checklist" : step === 2 ? "Gegevens invullen" : "PDF klaar"}
        </span>
      </div>

      {/* EU detected banner */}
      {isEuTransport && (
        <div className="mt-5 rounded-2xl border border-[hsl(var(--sand))]/40 bg-[hsl(var(--sand))]/10 p-4 text-[13px] leading-relaxed text-[hsl(var(--sand-dark))]">
          <strong>Internationaal transport gedetecteerd</strong> — {c.name} → {dest.name}. Volgens het NVWA-stappenplan moet je 6 weken vóór vertrek beginnen met de voorbereidingen. We helpen je met een complete pre-flight-checklist + multi-page PDF.
        </div>
      )}

      {/* STEP 1 — NVWA checklist */}
      {step === 1 && isEuTransport && (
        <div className="mt-8 space-y-6" data-testid="transport-doc-step-1">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
              <Info className="mr-1 inline h-3 w-3" /> NVWA pre-flight checks
            </p>
            <p className="mt-2 text-[13px] text-[hsl(var(--ink-soft))]">
              Doorloop deze 6 vragen op basis van het officiële NVWA-stappenplan EU-transport. Antwoorden worden verwerkt in een dedicated checklist-pagina in je PDF.
            </p>
            <div className="mt-5 space-y-4">
              {CHECKLIST.map((q) => (
                <div key={q.name} className="rounded-xl border border-[hsl(var(--border))]/60 bg-[hsl(var(--surface))] p-4">
                  <p className="font-semibold text-[hsl(var(--ink))]">{q.label}</p>
                  <p className="mt-1 text-[12px] text-[hsl(var(--ink-soft))]">{q.help}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {q.type === "yesno-na" && (
                      <ChecklistChip name={q.name} value="na" label="N.v.t." current={checklist[q.name]} onChange={setCl} />
                    )}
                    {(q.type === "yesno-unknown") && (
                      <ChecklistChip name={q.name} value="unknown" label="Onbekend" current={checklist[q.name]} onChange={setCl} />
                    )}
                    <ChecklistChip name={q.name} value="yes" label="Ja" current={checklist[q.name]} onChange={setCl} tone="ok" />
                    <ChecklistChip name={q.name} value="no" label="Nee" current={checklist[q.name]} onChange={setCl} tone="warn" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Transport-Verordening derivation */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
              <Clock className="mr-1 inline h-3 w-3" /> Transport-Verordening
            </p>
            <p className="mt-2 text-[13px] text-[hsl(var(--ink-soft))]">
              Op basis van de aard en duur van het transport bepalen we automatisch welke vergunningen en documenten je nodig hebt.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <span className="text-[11px] text-[hsl(var(--ink-soft))]">Type transport</span>
                <select
                  value={checklist.transportType}
                  onChange={(e) => setCl("transportType", e.target.value)}
                  data-testid="checklist-transport-type"
                  className="mt-1 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:border-[hsl(var(--forest))] focus:outline-none"
                >
                  <option value="">—</option>
                  {TRANSPORT_TYPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                </select>
              </div>
              <div>
                <span className="text-[11px] text-[hsl(var(--ink-soft))]">Verwachte afstand / duur</span>
                <select
                  value={checklist.duration}
                  onChange={(e) => setCl("duration", e.target.value)}
                  data-testid="checklist-duration"
                  className="mt-1 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:border-[hsl(var(--forest))] focus:outline-none"
                >
                  <option value="">—</option>
                  {DURATIONS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                </select>
              </div>
            </div>

            {/* Derived docs */}
            {derivedDocs.length > 0 && (
              <ul className="mt-5 space-y-1.5 text-[13px]" data-testid="derived-docs">
                {derivedDocs.map((d, i) => (
                  <li key={i} className="flex gap-2">
                    {d.ok ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--forest))]" strokeWidth={1.8} />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" strokeWidth={1.8} />
                    )}
                    <span className={d.ok ? "text-[hsl(var(--ink))]" : "text-amber-900"}>{d.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* TRACES extras */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
              <ShieldAlert className="mr-1 inline h-3 w-3" /> TRACES + exportkeuring
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <span className="text-[11px] text-[hsl(var(--ink-soft))]">Certificaattype</span>
                <select
                  value={checklist.tracesType}
                  onChange={(e) => setCl("tracesType", e.target.value)}
                  data-testid="checklist-traces-type"
                  className="mt-1 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:border-[hsl(var(--forest))] focus:outline-none"
                >
                  {TRACES_TYPES.map((x) => (
                    <option key={x.value} value={x.value} disabled={x.value === "30d_fei" && checklist.registered !== "yes"}>
                      {x.label}{x.value === "30d_fei" && checklist.registered !== "yes" ? " · alleen bij FEI-paspoort" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 text-[13px] text-[hsl(var(--ink))]">
                  <input
                    type="checkbox"
                    checked={checklist.departWithin48h}
                    onChange={(e) => setCl("departWithin48h", e.target.checked)}
                    data-testid="checklist-depart-48h"
                  />
                  Ik vertrek binnen 48 uur na de exportkeuring
                </label>
                <label className="inline-flex items-center gap-2 text-[13px] text-[hsl(var(--ink))]">
                  <input
                    type="checkbox"
                    checked={checklist.tracesDestinationValid}
                    onChange={(e) => setCl("tracesDestinationValid", e.target.checked)}
                    data-testid="checklist-traces-dest-valid"
                  />
                  Bestemmingslocatie staat in TRACES als GELDIG
                </label>
              </div>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-[hsl(var(--ink-soft))]">
              Bron:{" "}
              <a
                href="https://www.nvwa.nl/onderwerpen/dier/paarden/op-reis-met-mijn-paard/stappenplan-met-mijn-paard-op-reis-binnen-de-europese-unie-eu"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                NVWA-stappenplan EU-transport ↗
              </a>
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[12px] text-[hsl(var(--ink-soft))]">
              {checklistComplete
                ? "✓ Checklist compleet — door naar gegevens."
                : "Beantwoord alle vragen om door te gaan."}
            </p>
            <button
              onClick={() => setStep(2)}
              disabled={!checklistComplete}
              data-testid="transport-doc-step-1-next"
              className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--forest))] px-5 py-2.5 text-sm font-semibold text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))] disabled:opacity-40"
            >
              Volgende: gegevens
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — Data details */}
      {step === 2 && (
        <div className="mt-8 space-y-7" data-testid="transport-doc-step-2">
          <p className="text-sm text-[hsl(var(--ink-soft))]">
            Vul je gegevens hieronder in en download direct een professionele PDF met uniek
            documentnummer, QR-code en handtekening-velden. Geen account nodig,
            gegevens blijven in je browser.
          </p>

          {DATA_SECTIONS.filter((s) => !s.euOnly || isEuTransport).map((s) => (
            <fieldset key={s.key} className="space-y-3">
              <legend className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--forest))]">
                {s.label}
              </legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {s.fields.map((f) => (
                  <label key={f.name} className="block">
                    <span className="text-[11px] text-[hsl(var(--ink-soft))]">
                      {f.label}{f.required && " *"}
                    </span>
                    {f.type === "select" ? (
                      <select
                        value={data[f.name] || ""}
                        onChange={(e) => set(f.name, e.target.value)}
                        data-testid={`field-${f.name}`}
                        className="mt-1 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:border-[hsl(var(--forest))] focus:outline-none"
                      >
                        <option value="">—</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={f.type || "text"}
                        value={data[f.name] || ""}
                        onChange={(e) => set(f.name, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`field-${f.name}`}
                        className="mt-1 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:border-[hsl(var(--forest))] focus:outline-none"
                      />
                    )}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}

          {warning && (
            <div
              data-testid="transport-doc-warning"
              className={`rounded-xl border p-4 text-[13px] leading-relaxed ${
                warning.tone === "warn"
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-900"
                  : "border-[hsl(var(--forest))]/30 bg-[hsl(var(--forest))]/5 text-[hsl(var(--ink))]"
              }`}
            >
              <strong className="font-semibold">
                {warning.tone === "warn" ? "Let op" : "Goed om te weten"}:
              </strong>{" "}
              {warning.text}
            </div>
          )}

          {missing.length > 0 && (
            <p className="text-xs text-amber-700" data-testid="transport-doc-missing">
              Vul nog in: {missing.join(" · ")}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            {isEuTransport ? (
              <button
                onClick={() => setStep(1)}
                data-testid="transport-doc-back"
                className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm text-[hsl(var(--ink))] hover:bg-[hsl(var(--surface))]"
              >
                <ArrowLeft className="h-4 w-4" />
                Terug: checklist
              </button>
            ) : <span />}
            <button
              onClick={generate}
              disabled={busy || missing.length > 0}
              data-testid="transport-doc-generate"
              className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--forest))] px-6 py-3 text-sm font-semibold text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))] disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {busy ? "Genereren…" : "Genereer vervoersdocument (PDF)"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Success */}
      {step === 3 && success && (
        <div className="mt-8 rounded-2xl border border-[hsl(var(--forest))]/30 bg-[hsl(var(--forest))]/5 p-6 text-center" data-testid="transport-doc-step-3">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[hsl(var(--forest))]" strokeWidth={1.6} />
          <h3 className="mt-3 font-display text-2xl text-[hsl(var(--ink))]">
            PDF gedownload — succes!
          </h3>
          <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">
            Je multi-page vervoersdocument inclusief NVWA-checklist is opgeslagen.
            Controleer altijd de actuele regelgeving van de {c.regulator}.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => { setSuccess(false); setStep(2); }}
              data-testid="transport-doc-generate-again"
              className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm text-[hsl(var(--ink))]"
            >
              Nog een document maken
            </button>
            <button
              onClick={generate}
              disabled={busy}
              data-testid="transport-doc-redownload"
              className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--forest))] px-4 py-2 text-sm font-semibold text-[hsl(var(--background))]"
            >
              <Download className="h-4 w-4" /> Opnieuw downloaden
            </button>
          </div>
        </div>
      )}

      <p className="mt-8 text-[10px] leading-relaxed text-[hsl(var(--ink-soft))]">
        <strong>Disclaimer:</strong> deze tool helpt bij het opstellen van een
        vervoersdocument volgens algemene EU-richtlijnen + de NVWA-stappenplan voor
        EU-transport. De gebruiker is verantwoordelijk voor de juistheid en de
        actuele regelgeving in het land van bestemming en herkomst. EquiSight is
        geen vervanging voor juridisch advies. Officiële regelgeving:{" "}
        <a href={c.url} target="_blank" rel="noopener noreferrer" className="underline">
          {c.regulator}
        </a>
        {isEuTransport && (
          <>
            {" · "}
            <a
              href="https://www.nvwa.nl/onderwerpen/dier/paarden/op-reis-met-mijn-paard/stappenplan-met-mijn-paard-op-reis-binnen-de-europese-unie-eu"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              NVWA-stappenplan EU ↗
            </a>
          </>
        )}
        .
      </p>
    </div>
  );
};

/** Single chip button used inside the NVWA checklist questions. */
const ChecklistChip = ({ name, value, label, current, onChange, tone }) => {
  const active = current === value;
  const toneCls = active
    ? tone === "ok"
      ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))] text-[hsl(var(--background))]"
      : tone === "warn"
        ? "border-amber-500 bg-amber-500 text-white"
        : "border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--background))]"
    : "border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--ink))]";
  return (
    <button
      type="button"
      onClick={() => onChange(name, value)}
      data-testid={`checklist-${name}-${value}`}
      className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-all ${toneCls}`}
    >
      {label}
    </button>
  );
};

export default TransportDocTool;
